import { startDomTranslation, langSelect } from './i18n';
import { App } from './app/App';
import { PlayHud } from './app/PlayHud';
import { quickHuman } from './spike/quickHuman';
import { installDevtools } from './app/devtools';
import type { Character } from './app/Character';

// Langue (anglais par défaut) : la page et tout ce qui y apparaît ensuite ; sélecteur EN / FR / ES dans la barre.
startDomTranslation();
document.querySelector('.hud-right')?.prepend(langSelect());

const app = new App(document.getElementById('stage')!);
(window as unknown as { app: App }).app = app;
const hud = new PlayHud(app);
let character: Character | undefined;
/** Sa voix (synthèse vocale, lèvres, répliques). */
let talk: import('./voice').Talk | undefined;
if (import.meta.env.DEV || new URLSearchParams(location.search).has('dev')) installDevtools(app, () => character);

/** Noms de poses utilisés par les scénarios → identifiants de la bibliothèque. */
const SCENARIO_POSES: Record<string, string> = {
  idle_hand_on_hip: 'idle/hand-hip', idle_arms_crossed: 'idle/arms-crossed', stand: 'idle/weight-shift', relaxed: 'idle/relaxed',
};

/** Paysage sonore demandé par le décor (pluie, feu, vinyle…), rejoué dès que le son est activé. */
let lastSoundscape = '';
let onSoundscape: (hint: string) => void = (h) => { lastSoundscape = h; };

/** Bouton "Son" : ambiance procédurale (désactivée par défaut, activée par un geste utilisateur). */
function wireSound(a: App, c: Character) {
  const btn = document.getElementById('sound') as HTMLButtonElement;
  let amb: import('./audio/Ambience').Ambience | undefined;
  const sync = () => { if (amb) { amb.setMood(c.presence.mood.id); } };
  const prevMood = c.presence.onMoodChange;
  c.presence.onMoodChange = (m) => { prevMood(m); amb?.setMood(m.id); };
  const prevAmb = a.stage.onAmbiance;
  a.stage.onAmbiance = (id) => { prevAmb(id); amb?.setScene(id); };
  onSoundscape = (h) => { lastSoundscape = h; amb?.setSoundscape(h); };
  btn.addEventListener('click', async () => {
    // Avant tout await : la synthèse vocale doit être débloquée dans le geste utilisateur (iOS, Safari).
    talk?.setSound(!(amb?.enabled ?? false));
    if (!amb) { const { Ambience } = await import('./audio/Ambience'); amb = new Ambience(); sync(); if (lastSoundscape) amb.setSoundscape(lastSoundscape); }
    if (amb.enabled) amb.disable(); else await amb.enable();
    btn.setAttribute('aria-pressed', String(amb.enabled));
    btn.textContent = amb.enabled ? '♪ Son activé' : '♪ Son';
  });
}

async function boot() {
  app.loader.set(0.02, 'Mise en place du plateau…');
  const spike = new URLSearchParams(location.search).has('spike');
  if (!spike) {
    // Module humain complet : squelette, morphs, présence, toucher.
    const { Character, initialCharacterState } = await import('./app/Character');
    performance.mark('boot:character-module');
    const stop = app.loader.trackNetwork(0.05, 0.97);
    try {
      const { state } = await initialCharacterState();
      character = await Character.create(app, state);
    performance.mark('boot:character-created');
    } finally { stop(); }
    const [{ Modes }, { ScenarioMenu }, { PoseMenu }] = await Promise.all([import('./app/Modes'), import('./app/ScenarioMenu'), import('./app/PoseMenu')]);
    new PoseMenu(app, character);
    performance.mark('boot:menus');
    const { PostureMenu } = await import('./app/PostureMenu');
    const postureMenu = new PostureMenu(app, character.tweaks);
    // La placer à la souris : glisser au sol, molette pour la tourner (touche D).
    const { PlaceMode } = await import('./app/PlaceMode');
    const placeMode = new PlaceMode(app, character);
    // On ne la place que debout : assise ou allongée, elle va d'abord à un emplacement « debout » du décor.
    const placed = character;
    const openPlace = () => {
      if (placeMode.active) { placeMode.toggle(false); return; }
      const standing = placed.animator.grounding === 'standing' && (!anchorNow || anchorNow.kind === 'stand');
      const st = standing ? null : decor.instance?.anchors?.find((x) => x.kind === 'stand');
      if (!st) { placeMode.toggle(true); return; }
      void import('./app/toast').then(({ toast }) => toast('Elle se lève… tu pourras la placer ensuite.', 2600));
      void Promise.resolve(decor.setAnchor(st.id)).then(() => setTimeout(() => placeMode.toggle(true), 400));
    };
    postureMenu.onPlace = openPlace;
    window.addEventListener('keydown', (e) => {
      if ((e.target as HTMLElement)?.closest?.('input,textarea,select') || document.body.classList.contains('creating')) return;
      if ((e.key === 'd' || e.key === 'D') && !e.ctrlKey && !e.metaKey && !e.altKey) openPlace();
    });
    (window as unknown as { placeMode: unknown }).placeMode = placeMode;
    // Ta main sur elle : elle s'y plaque, la peau se creuse, les parties souples cèdent (touche M).
    const { HandMode } = await import('./app/HandMode');
    const handMode = new HandMode(app, character);
    postureMenu.onHand = () => { if (placeMode.active) placeMode.toggle(false); handMode.toggle(); };
    placeMode.onChange = (on) => { if (on) handMode.toggle(false); else postureMenu.refresh(); };
    window.addEventListener('keydown', (e) => {
      if ((e.target as HTMLElement)?.closest?.('input,textarea,select') || document.body.classList.contains('creating')) return;
      if ((e.key === 'm' || e.key === 'M') && !e.ctrlKey && !e.metaKey && !e.altKey) postureMenu.onHand();
    });
    (window as unknown as { handMode: unknown }).handMode = handMode;
    // Mode Jouer : elle vit sur une animation d'attente (appui alterné, pieds plantés).
    void character.playPose('idle/weight-shift', 0, true);
    const her = character;
    // Décors procéduraux (fondu au noir, construction en tâche de fond, précompilation des shaders).
    const [{ DecorManager, DecorPicker }, { DecorMenu }] = await Promise.all([import('./scenes'), import('./app/DecorMenu')]);
    let decorMenu: InstanceType<typeof DecorMenu> | undefined;
    // Un objet du décor s'allume / s'éteint (par toi ou par elle) : elle regarde, réagit, commente.
    const reactToItem = (item: import('./scenes').DecorInteractive, on: boolean) => {
      her.presence.glanceAt(item.object.getWorldPosition(item.object.position.clone()), 1.2);
      her.presence.react(item.kind === 'musique' ? 'clin' : on ? 'rire' : 'surprise');
      talk?.onEvent('objet', { id: item.id, tag: `${item.kind ?? 'lampe'}:${on ? 'on' : 'off'}` });
      // Le tourne-disque lance la musique : elle a envie de danser.
      if (item.kind === 'musique' && on && her.presence.mood.id !== 'melancolique' && Math.random() < 0.6) setTimeout(() => void her.startDance(98), 1500);
      if (item.kind === 'musique' && !on) her.stopDance();
    };
    // Emplacements du décor (canapé, lit, rambarde…) : c'est le décor qui se déplace sous elle ; elle prend la pose.
    let anchorNow: import('./scenes').DecorAnchor | null = null;
    const poseForAnchor = async (a: import('./scenes').DecorAnchor) => {
      anchorNow = a;
      const pose = a.poses?.[0] ?? (a.kind === 'sit' ? 'poses/callharvey3d_sittingnatural'
        : a.kind === 'lie' ? 'allongee/cote' : a.kind === 'floor' ? 'sol/amazone' : 'idle/weight-shift');
      her.stopDance();
      her.poses.setAnchor(a); // hauteur / distance de la rambarde pour les poses accoudée / adossée
      await her.playPose(pose, 0, a.kind === 'stand' || a.kind === 'lean');
      // Meuble du décor : le pouf reste calculé (drapé) mais invisible. Sans siège (studio), elle apporte son pouf.
      her.seat.hidden = !!a.seat || a.kind === 'lie';
      await new Promise((r) => requestAnimationFrame(() => r(null)));
      return a.seat ? her.animator.seat?.height ?? null : null;
    };
    const decor = new DecorManager({ renderer: app.renderer.gl, stage: app.stage, quality: app.renderer.quality }, {
      onSwap: (_id, inst) => { if (inst.camera) app.director.frame('upper', 1.2); },
      onAnchor: (a, _inst, o) => {
        if (o.decorChange) { const was = anchorNow; anchorNow = a; her.seat.hidden = false; if (!was || was.kind === 'stand') return null; }
        return poseForAnchor(a);
      },
      onInteract: (_id, on, item) => reactToItem(item, on),
      onSoundscape: (hint) => onSoundscape(hint),
      onWeather: (_decor, id) => talk?.onEvent('meteo', { tag: id }),
      onChange: (id) => {
        decorMenu?.sync(id);
        talk?.onEvent('decor', { id });
        // Tes préférences retenues : la mer (plage), la pluie…
        try {
          const facts = JSON.parse(localStorage.getItem('simulacre.souvenirs') ?? '[]') as Array<{ kind: string; what: string }>;
          const pref = (w: string) => facts.some((f) => f.kind === 'prefere' && f.what.startsWith(w));
          if (id === 'plage' && pref('la mer')) setTimeout(() => talk?.say('Tu préfères la mer, je m’en souviens. Elle est belle, ce soir.', { subtitle: true }), 5200);
          if ((id === 'chambre' || id === 'salon') && pref('les livres')) setTimeout(() => talk?.say('Il y a des livres, ici… Je te lis une histoire, plus tard ?', { subtitle: true }), 6000);
        } catch { /* ignoré */ }
        // Brise dans les cheveux dehors (plage, balcon, forêt) ; air immobile à l'intérieur.
        her.hair.wind = ({ plage: 0.9, balcon: 0.55, foret: 0.35, jardin: 0.25 } as Record<string, number>)[id] ?? 0;
        try { localStorage.setItem('simulacre.decor', id); } catch { /* ignoré */ }
        // La poussière en suspension est propre au studio (ailleurs, les décors ont leurs particules).
        app.stage.setDust(id === 'studio' && app.renderer.quality.dust);
      },
    });
    app.updaters.push((dt, t) => decor.update(dt, t));
    (window as unknown as { decor: unknown }).decor = decor;
    // Elle va à pied d'un emplacement à l'autre (même décor) : se lève, contourne les meubles, s'assoit en reculant.
    const { AnchorWalk } = await import('./anim/AnchorWalk');
    const fadeToAnchor = decor.setAnchor.bind(decor);
    const stroll = new AnchorWalk(her, decor, {
      fadeTo: (id) => fadeToAnchor(id),
      onLeave: () => { anchorNow = null; },
      onStart: () => app.director.frame('full', 1.4, 65),
      onArrive: (a) => { anchorNow = a; app.director.frame(a.kind === 'stand' || a.kind === 'lean' ? 'upper' : 'body', 1.6, a.kind === 'lie' ? 70 : 15); },
    });
    app.updaters.push(() => stroll.update()); // après la mise à jour du personnage
    // Hauteur du coussin sous elle (pour que la chair s'aplatisse dessus) : repère du décor → repère personnage.
    app.updaters.push(() => {
      const g = decor.instance?.group, a = anchorNow;
      her.seatTop = g && a?.seat && !stroll.busy ? g.position.y + a.position.y + a.seat.height : null;
    });
    decor.setAnchor = (id: string, o: { instant?: boolean } = {}) => {
      if (!o.instant && stroll.goTo(id)) return stroll.arrival;
      stroll.cancel();
      return fadeToAnchor(id, o);
    };
    (window as unknown as { stroll: unknown }).stroll = stroll;
    // Bruits de pas pendant la marche (selon le sol du décor), si le son est activé.
    const { Footsteps, groundOf } = await import('./audio/Footsteps');
    const steps = new Footsteps();
    let lastStance = -1, lastStepT = 0;
    app.updaters.push(() => {
      const st = stroll.walker.step;
      if (!st.walking) { lastStance = -1; return; }
      if (st.stance !== lastStance) {
        const now = performance.now();
        if (lastStance >= 0 && now - lastStepT > 180 && document.getElementById('sound')?.getAttribute('aria-pressed') === 'true') {
          steps.step(groundOf(decor.current), 0.8 + Math.random() * 0.2, st.stance === 0 ? -1 : 1);
        }
        lastStance = st.stance; lastStepT = now;
      }
    });
    // Jupe qui se balance en marchant : s'évase autour des jambes, suit le déhanché avec un temps de retard (ressort).
    const { windUniforms } = await import('./render/materials/provider');
    let walkK = 0, skirtX = 0, skirtV = 0;
    app.updaters.push((dt) => {
      const st = stroll.walker.step;
      walkK += ((st.walking ? 1 : her.dance.dancing ? 0.6 : 0) - walkK) * (1 - Math.exp(-dt * 3));
      if (walkK < 1e-3 && Math.abs(skirtX) < 1e-4) { windUniforms.uWalk.value.set(0, 0, 0); return; }
      const side = st.stance === 0 ? 1 : -1;
      const goal = st.walking ? -0.03 * side * Math.sin(Math.PI * Math.min(1, st.t)) * walkK : 0;
      const h = Math.min(dt, 0.05), k = 55, c = 2 * 0.35 * Math.sqrt(k);
      skirtV += (k * (goal - skirtX) - c * skirtV) * h;
      skirtX += skirtV * h;
      windUniforms.uWalk.value.set(skirtX, 0.05 * walkK, 0.008 * walkK);
    });
    // Double-clic au sol : elle y va à pied.
    const { WalkHere } = await import('./app/WalkHere');
    const walkHere = new WalkHere(app, her, {
      instance: () => decor.instance,
      navGrid: () => stroll.navGrid(),
      goTo: (id) => { void decor.setAnchor(id); },
      blocked: () => stroll.busy || her.dance.dancing || scenarios.player.playing || her.caress.active,
      refused: () => { void import('./app/toast').then(({ toast }) => toast('Elle ne peut pas aller là.', 1800)); },
    });
    // Drapé des jupes au sol, à genoux, allongée (Character ne drape que sur un siège).
    let floorDrape: object | null = null, floorHold = 0, floorEpoch = -1, seenEpoch = -1, floorHoldFor: object | null = null;
    app.updaters.push((dt) => {
      const a = her.animator, h = her.human;
      if (!h.drape) return;
      // Assise sur un siège : c'est Character qui drape. Au sol, à genoux, allongée (même avec un coussin) : ici.
      const want = a.grounding !== 'standing' && !(a.grounding === 'sitting' && her.seat.shape) && !a.settling && !stroll.busy ? a.current : null;
      if (her.drapeEpoch !== seenEpoch) { seenEpoch = her.drapeEpoch; floorHold = 0; }
      // Pas (ou plus) au sol : on ne relâche que NOTRE drapé (celui du siège est géré par Character).
      if (!want) { if (floorDrape) { floorDrape = null; h.undrape?.(); } floorHold = 0; floorEpoch = her.drapeEpoch; return; }
      if (want === floorDrape && her.drapeEpoch === floorEpoch) { floorHold = 0; return; }
      if (floorDrape && want !== floorDrape) { floorDrape = null; h.undrape?.(); }
      if (want !== floorHoldFor) { floorHoldFor = want; floorHold = 0; }
      if ((floorHold += dt) < 0.3) return;
      floorHold = 0; floorDrape = want; floorEpoch = her.drapeEpoch; h.drape(her.seat.shape, { sweep: false });
    });
    // Hauteur d'assise réelle connue après analyse de la pose : le décor se recale (±12 cm).
    const prevSupport = her.animator.onSupport;
    her.animator.onSupport = (seat, g) => { prevSupport(seat, g); if (anchorNow?.seat && seat) decor.fitSeat(seat.height); };
    // Objets cliquables du décor (elle reste prioritaire sous le pointeur).
    new DecorPicker(app.renderer.gl.domElement, app.camera, () => decor.instance, {
      blocker: (ndc) => her.behavior.touch.pick(ndc, app.camera)?.distance ?? null,
      enabled: () => !document.body.classList.contains('creating') && !document.body.classList.contains('photo'),
    });
    performance.mark('boot:decor-manager');
    const applyQ = app.applyQuality.bind(app);
    app.applyQuality = (tier) => { applyQ(tier); decor.setQuality(app.renderer.quality); };
    decorMenu = new DecorMenu(decor);
    // Elle reste là où tu l'as laissée.
    let savedDecor = 'studio';
    try { savedDecor = localStorage.getItem('simulacre.decor') ?? 'studio'; } catch { /* défaut */ }
    if (savedDecor !== 'studio' && decor.list.some((d) => d.id === savedDecor)) void decor.set(savedDecor);
    setTimeout(() => void decor.prewarm(), 4000);
    const scenarios = new ScenarioMenu(app, character, {
      setPose: (id, fade) => void her.playPose(SCENARIO_POSES[id] ?? id, fade, true),
      setDecor: (id) => decor.set(id),
      currentDecor: () => decor.current,
    });
    // Pendant un scénario, c'est lui qui dirige sa posture.
    const sp = scenarios.player;
    const prevEnd = sp.onEnd;
    // Mode cinéma pendant un scénario : bandes noires, interface effacée (titre, arrêt et sous-titres restent).
    sp.onStart = () => { her.autoPosture = false; her.stopDance(); document.body.classList.add('cinema'); };
    sp.onEnd = () => { prevEnd(); her.autoPosture = true; document.body.classList.remove('cinema'); };
    wireSound(app, character);
    performance.mark('boot:scenarios');
    // Voix : toucher, proximité, silence, retour, humeur, tenue, scénarios (répliques entre « »).
    const { Talk, VoiceSettings } = await import('./voice');
    talk = new Talk(app, character);
    talk.bindScenario(scenarios.player);
    performance.mark('boot:voice');
    const voiceBtn = document.createElement('button');
    voiceBtn.className = 'chip';
    voiceBtn.id = 'voice-btn';
    voiceBtn.textContent = 'Voix';
    voiceBtn.title = 'Sa voix : timbre, débit, volume, bavardage, sous-titres';
    voiceBtn.dataset.icon = '❝';
    document.querySelector('.hud-right')?.insertBefore(voiceBtn, document.getElementById('quality'));
    new VoiceSettings(talk).attach(voiceBtn);
    // Gestes de vie : mèche, nuque, étirement, jambes croisées… (pilote automatique selon l'humeur).
    const { Gestures, GestureDirector } = await import('./gestures');
    const gestures = new Gestures({ character });
    const gestureDirector = new GestureDirector(gestures, character);
    app.updaters.push((dt) => { gestures.update(dt); gestureDirector.update(dt); });
    // Mains posées sur elle (joues, menton, cœur, cuisses…) : la peau et le tissu se creusent doucement sous la paume.
    const handPress = [-1, -1];
    gestures.onPress = (k, center, depth) => {
      const sp = her.press;
      if (depth > 0) {
        if (handPress[k] < 0) handPress[k] = sp.press(center, { depth, radius: 0.026, freq: 4.5, damping: 0.75 });
        else { sp.move(handPress[k], center); sp.setDepth(handPress[k], depth); }
      } else if (handPress[k] >= 0) { sp.release(handPress[k]); handPress[k] = -1; }
    };
    performance.mark('boot:gestures');
    // Le décor dans ses gestes : du vent → elle replace sa mèche ; fraîcheur (nuit à la plage, brume, pluie) → frissons.
    const COLD = new Set(['plage:lune', 'foret:brume', 'balcon:bruine', 'balcon:averse', 'foret:aube']);
    gestureDirector.contextual = () => {
      const out: Array<[string, number]> = [];
      if (her.hair.wind > 0.3) out.push(['meche', 1.5 * her.hair.wind]);
      if (COLD.has(decor.current + ':' + decor.weather)) out.push(['frisson', 1.6]);
      return out;
    };
    // Son prénom et son caractère : bavardage, vivacité ; carte d'accueil à la première visite.
    const { PersonaDialog, TRAITS } = await import('./app/Persona');
    const applyPersona = (p: { trait: keyof typeof TRAITS }) => {
      if (talk) talk.chattiness = TRAITS[p.trait].chat;
      gestureDirector.pace = TRAITS[p.trait].gestures;
    };
    character.onPersona = applyPersona;
    if (character.persona) applyPersona(character.persona);
    const personaDialog = new PersonaDialog(character);
    personaDialog.onChange = (p, first) => {
      her.presence.react('rougir');
      setTimeout(() => talk?.say(first ? `${p.name}… J’aime bien. Ça me ressemble.` : `${p.name}. C’est moi.`), 900);
    };
    const moodEl = document.getElementById('mood');
    if (moodEl) {
      moodEl.setAttribute('role', 'button');
      moodEl.tabIndex = 0;
      moodEl.title = 'Son prénom et son caractère';
      moodEl.addEventListener('click', () => personaDialog.open(false));
      moodEl.addEventListener('keydown', (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); personaDialog.open(false); } });
    }
    window.addEventListener('simulacre:persona', () => personaDialog.open(false));
    // « Quoi de neuf ? » d'abord (une fois par version), puis la carte « Fais sa connaissance » à la première visite.
    const { WhatsNew } = await import('./app/WhatsNew');
    const whatsNew = new WhatsNew();
    window.addEventListener('simulacre:whatsnew', () => whatsNew.open());
    const greetPersona = () => { if (PersonaDialog.shouldGreet() && !document.body.classList.contains('photo')) personaDialog.open(true); };
    if (whatsNew.maybeShow(3500)) whatsNew.onClose = () => { whatsNew.onClose = () => {}; setTimeout(greetPersona, 1500); };
    else if (PersonaDialog.shouldGreet()) setTimeout(greetPersona, 9000);
    (window as unknown as { persona: unknown }).persona = personaDialog;
    // Quand elle pivote vers toi, un petit pas accompagne le mouvement.
    character.onTurn = () => { gestures.play('pas'); };
    // Les scénarios peuvent demander un geste de vie précis.
    scenarios.player.hooks.playGesture = (id) => (gestures.play(id) ? 3.6 : 0);
    const prevTouch = character.behavior.onTouch;
    character.behavior.onTouch = (hit) => { prevTouch(hit); gestureDirector.onEvent('toucher'); };
    const admire = character.behavior.admireOutfit.bind(character.behavior);
    character.behavior.admireOutfit = () => { admire(); gestureDirector.onEvent('tenue'); };
    character.behavior.onReturn = () => {
      // Assise d'elle-même : elle se lève pour t'accueillir, puis te fait signe.
      if (her.welcomeBack()) setTimeout(() => gestureDirector.onEvent('retour'), 1600);
      else gestureDirector.onEvent('retour');
    };
    // Caresse : elle en parle (tendrement), ou pose ses limites sur les zones intimes.
    character.caress.onStart = (zone) => {
      if (zone === 'poitrine' || zone === 'ventre' || zone === 'hanches') { talk?.onEvent('toucher', { zone, insistent: true }); gestureDirector.onEvent('toucher'); }
      else talk?.onEvent('caresse', { tag: zone === 'visage' ? 'visage' : zone === 'tete' ? 'cheveux' : 'bras' });
    };
    character.caress.onEnd = (zone, secs) => {
      const sensitive = zone === 'poitrine' || zone === 'ventre' || zone === 'hanches';
      her.bond.add(sensitive ? -0.04 : Math.min(0.02, secs * 0.004));
      if (secs > 4 && !sensitive) talk?.onEvent('caresse', { tag: 'fin' });
    };
    // Un palier de complicité franchi : elle le remarque.
    her.bond.onStage = (stage, up) => {
      her.applyMoodBias();
      if (!up) return;
      her.presence.react('rougir');
      setTimeout(() => talk?.reply(stage.hearts >= 4 ? 'Tu sais… je tiens vraiment à toi.' : stage.hearts === 3 ? 'Je me sens bien avec toi. C’est rare.' : 'J’aime bien quand tu es là.'), 1200);
    };
    const { PhotoMode } = await import('./app/PhotoMode');
    // Mode photo : menus fermés, et elle se tait (sinon ses lèvres bougent sur la photo).
    const photo = new PhotoMode(app, character, () => { for (const m of document.querySelectorAll('.scenario-menu.on')) m.classList.remove('on'); talk?.hush(); });
    // Lui parler : intentions reconnues (bonjour, compliments, assieds-toi, un bisou…), réponse parlée + geste.
    const { Conversation } = await import('./app/Conversation');
    // Soirée film : elle s'installe, éteint les lampes, la lueur de l'écran sur son visage, ses réactions.
    const { MovieNight } = await import('./app/MovieNight');
    const movie = new MovieNight(app, her, {
      talk, anchors: () => decor.anchors(), anchor: () => decor.anchor,
      goTo: (id) => decor.setAnchor(id), interactives: () => decor.interactives, gesture: (id) => gestures.play(id),
    });
    (window as unknown as { movie: unknown }).movie = movie;
    window.addEventListener('simulacre:film', () => { if (movie.active) movie.stop(true); else void movie.start(); });
    // « Tape m'en cinq » : main levée ; clic sur sa main → tape (son), rire ; sinon elle abandonne au bout de 7 s.
    let fiveTimer = 0;
    const highFive = () => {
      if (gestures.playing && gestures.playing !== 'tchek') gestures.stop(0.25);
      const go = () => {
        if (!gestures.play('tchek')) return false;
        clearTimeout(fiveTimer);
        fiveTimer = window.setTimeout(() => {
          if (gestures.playing === 'tchek' && gestures.holding) { gestures.release(); talk?.reply('Tu me laisses en plan ?'); her.behavior.holdMood('boudeuse', 8); }
        }, 7000);
        return true;
      };
      if (go()) return true;
      setTimeout(go, 320);
      return true;
    };
    const prevTouch5 = character.behavior.onTouch;
    character.behavior.onTouch = (hit) => {
      if (gestures.playing === 'tchek' && gestures.holding && (hit.zone === 'main' || hit.zone === 'bras')) {
        clearTimeout(fiveTimer);
        gestures.release();
        her.presence.react('rire');
        her.bond.add(0.004);
        if (document.getElementById('sound')?.getAttribute('aria-pressed') === 'true') clap();
        setTimeout(() => talk?.reply(['Yes !', 'Bien joué !', 'Ha ! On fait une bonne équipe.'][Math.floor(Math.random() * 3)]), 300);
        return;
      }
      prevTouch5(hit);
    };
    let clapCtx: AudioContext | null = null;
    const clap = () => {
      try {
        clapCtx ??= new AudioContext();
        const ctx = clapCtx, t0 = ctx.currentTime + 0.01, n = Math.floor(ctx.sampleRate * 0.12);
        const buf = ctx.createBuffer(1, n, ctx.sampleRate), d = buf.getChannelData(0);
        for (let i = 0; i < n; i++) d[i] = (Math.random() * 2 - 1) * Math.exp(-i / (ctx.sampleRate * 0.018));
        const src = ctx.createBufferSource(); src.buffer = buf;
        const f = ctx.createBiquadFilter(); f.type = 'bandpass'; f.frequency.value = 1500; f.Q.value = 0.8;
        const g = ctx.createGain(); g.gain.value = 0.5;
        src.connect(f).connect(g).connect(ctx.destination); src.start(t0);
      } catch { /* audio indisponible */ }
    };
    // Objets offerts : une rose, un thé, un livre — elle les tient, s'en sert, les pose.
    const { HeldProps } = await import('./props/HeldProps');
    const { Gifts } = await import('./app/Gifts');
    const held = new HeldProps(character, gestures);
    app.updaters.push((dt) => held.update(dt));
    const gifts = new Gifts(app, character, held, talk);
    (window as unknown as { gifts: unknown }).gifts = gifts;
    photo.props = { give: (k) => held.give(k), take: () => held.take(), get kind() { return held.kind; } };
    // Mes scènes : garder la mise en scène du moment, la retrouver plus tard.
    const { SceneShots } = await import('./app/SceneShots');
    (window as unknown as { scenes: unknown }).scenes = new SceneShots(app, her, decor, held);
    const conversation = new Conversation(app, character, {
      talk, gesture: (id) => gestures.play(id), openPhoto: () => photo.open(),
      give: (k) => gifts.give(k), takeBack: () => gifts.takeBack(),
      places: () => decor.anchors(), currentPlace: () => decor.anchor,
      goPlace: (id) => { void decor.setAnchor(id); }, comeHere: () => walkHere.comeHere(),
      highFive: () => highFive(),
      hold: (k) => held.give(k),
      orbit: (deg, dur) => app.director.orbit(deg, dur),
      movie: (on) => { if (on) void movie.start(); else movie.stop(true); return true; },
    });
    (window as unknown as { conversation: unknown; talk: unknown }).conversation = conversation;
    // Elle te pose des questions quand la barre est ouverte et que tu ne dis rien (une toutes les 3 min au plus).
    let askAcc = 0;
    app.updaters.push((dt) => { if ((askAcc += dt) < 5) return; askAcc = 0; conversation.maybeAsk((performance.now() - conversation.lastSayAt) / 1000); });
    (window as unknown as { talk: unknown }).talk = talk;
    // Réalité virtuelle / augmentée (boutons visibles seulement si l'appareil le permet) : son regard suit ta tête.
    const { XRMode } = await import('./app/XR');
    new XRMode(app, () => [her.human.root], (mode) => { if (mode) { conversation.toggle(false); photo.close(); } });
    const { Shortcuts } = await import('./app/Shortcuts');
    new Shortcuts();
    // Elle prend soin de toi : après ~45 min de présence continue, une invitation douce à faire une pause.
    let together = 0, nextCare = 45 * 60;
    app.updaters.push((dt) => {
      if (document.visibilityState !== 'visible') return;
      together += dt;
      if (together < nextCare || busyNow()) return;
      nextCare = together + (40 + Math.random() * 15) * 60;
      const late = new Date().getHours() >= 23 || new Date().getHours() < 5;
      her.presence.react('rougir');
      talk?.reply(late ? 'Il est tard, tu sais… J’aime t’avoir là, mais pense à dormir un peu.'
        : ['Ça fait un moment que tu es là… pense à boire un verre d’eau, d’accord ?', 'Tu devrais t’étirer un peu. Je t’attends, promis.', 'Fais une petite pause si tu veux. Je ne bouge pas.'][Math.floor(Math.random() * 3)]);
    });
    // Initiatives : laissée à elle-même, elle met un disque, allume des bougies, le feu de camp…
    const busyNow = () => document.body.classList.contains('creating') || document.body.classList.contains('photo')
      || scenarios.player.playing || her.caress.active || !!document.querySelector('.talk-bar.on, .persona-dialog.on, .scenario-menu.on');
    const { Initiatives } = await import('./app/Initiatives');
    new Initiatives(her, { interactives: () => decor.interactives, react: reactToItem, busy: () => busyNow() || her.sleeping });
    // Sommeil : allongée et laissée tranquille, elle s'endort (lampe éteinte, rêves) ; un clic ou un mot la réveille.
    const { SleepBehavior } = await import('./app/Sleep');
    const { anchorsOf } = await import('./scenes');
    const sleep = new SleepBehavior(her, {
      talk, busy: busyNow, interactives: () => decor.interactives,
      anchors: () => (decor.instance ? anchorsOf(decor.instance) : []),
      anchor: () => anchorNow,
      goTo: (id) => { void decor.setAnchor(id); },
      holding: () => held.busy,
    });
    app.updaters.push((dt) => sleep.update(dt));
    (window as unknown as { sleep: unknown }).sleep = sleep;
    // Nos moments : danse (après quelques secondes), soirée dans un décor (après 1 min 30) ; évocation au retour.
    const { addMoment, moments } = await import('./app/Moments');
    let danceT = 0, decorT = 0, decorSeen = '';
    app.updaters.push((dt) => {
      danceT = her.dance.dancing ? danceT + dt : 0;
      if (danceT > 8 && danceT - dt <= 8) addMoment('danse');
      if (decor.current !== decorSeen) { decorSeen = decor.current; decorT = 0; }
      decorT += dt;
      if (decorT > 90 && decorT - dt <= 90 && !moments.has(decorSeen, 6 * 3600_000)) addMoment(decorSeen);
    });
    // Un petit mot écrit à la main si tu reviens après plusieurs heures (sinon, elle évoque un souvenir à voix haute).
    const { LoveNote } = await import('./app/Note');
    const note = new LoveNote(her);
    note.maybeShow(3);
    setTimeout(() => {
      const line = moments.recallLine(true);
      if (line && !note.shown && !busyNow() && !her.sleeping) talk?.say(line, { subtitle: true });
    }, 26000);
    (window as unknown as { note: unknown }).note = note;
    (window as unknown as { moments: unknown }).moments = moments;
    // Météo vivante : sans choix de ta part depuis un moment, le temps change de lui-même (la pluie arrive, cesse…),
    // toutes les 10 à 20 min, doucement ; elle le remarque (réplique « météo »).
    let weatherT = 0, nextWeather = (10 + Math.random() * 10) * 60, weatherDecor = '';
    app.updaters.push((dt) => {
      if (decor.current !== weatherDecor) { weatherDecor = decor.current; weatherT = 0; }
      weatherT += dt;
      if (weatherT < nextWeather) return;
      weatherT = 0;
      nextWeather = (10 + Math.random() * 10) * 60;
      const ws = decor.weathers();
      if (ws.length < 2 || busyNow() || scenarios.player.playing || Date.now() - decor.userWeatherAt < 20 * 60_000) return;
      if (Math.random() < 0.45) return;
      const others = ws.filter((w) => w.id !== decor.weather);
      decor.setWeather(others[Math.floor(Math.random() * others.length)].id, { auto: true });
    });
    // Orage sous l'averse (balcon) : éclairs, tonnerre (si le son est activé), elle sursaute.
    const { Storm } = await import('./app/Storm');
    new Storm(app, her, {
      active: () => decor.current === 'balcon' && decor.weather === 'averse',
      soundOn: () => document.getElementById('sound')?.getAttribute('aria-pressed') === 'true',
      talk, wake: () => sleep.wake(false),
    });
    // Caméra d'ambiance : longs plans doux quand tu la laisses vivre sans rien toucher.
    const { AmbientCamera } = await import('./app/AmbientCamera');
    const ambientCam = new AmbientCamera(app, () => document.body.classList.contains('creating') || document.body.classList.contains('photo')
      || scenarios.player.playing || her.caress.active || !!document.querySelector('.talk-bar.on, .persona-dialog.on, .scenario-menu.on'));
    // Moins d'animations demandé par le système : pas de bougé à l'épaule ni de travellings automatiques.
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
    const applyReduce = () => { ambientCam.enabled = !reduce.matches; app.director.reducedMotion = reduce.matches; };
    applyReduce();
    reduce.addEventListener('change', applyReduce);
    const modes = new Modes(app, character, hud, () => { scenarios.player.stop(); photo.close(); conversation.toggle(false); her.stopDance(); }, scenarios.player, decor);
    // Look appliqué depuis la galerie (autre onglet) : elle l'adopte ici en direct.
    const { CHARACTER_STORE_KEY, parseStoredCharacter } = await import('./app/Character');
    const { toast } = await import('./app/toast');
    window.addEventListener('storage', async (e) => {
      if (e.key !== CHARACTER_STORE_KEY || !e.newValue) return;
      const { loadCatalog } = await import('./human');
      const next = parseStoredCharacter(e.newValue, (await loadCatalog('/assets/gen/')).entries);
      if (!next) return;
      await her.adopt(next);
      modes.refresh();
      toast('<b>Nouveau look</b> — elle l’a adopté.');
    });
    // Émotions re-validées dans le labo (Création > Émotions ↗) : appliquées en direct, sans reconstruire.
    {
      const { refreshValidated } = await import('./presence/expressions');
      const refreshFaces = async (announce: boolean) => {
        if (!(await refreshValidated())) return;
        her.presence.refreshFace();
        if (announce) toast('<b>Émotions</b> — ses nouvelles expressions sont prises en compte.');
      };
      void refreshFaces(false);
      try { new BroadcastChannel('simulacre-lab').onmessage = () => void refreshFaces(true); } catch { /* ignoré */ }
      window.addEventListener('focus', () => void refreshFaces(true));
    }
    (window as unknown as { her: unknown }).her = character;
  } else {
    const her = await quickHuman(app.materials.provider, (p, label) => app.loader.set(0.05 + p * 0.9, label));
    app.stage.scene.add(her);
    app.post.focusTarget.set(0, 1.5, 0.08);
  }
  performance.mark('boot:done');
  app.loader.done();
  app.start();
  // Laisse le voile du chargement se lever avant son premier regard.
  if (character) setTimeout(() => { character?.intro(); talk?.onEvent('arrivee'); }, 500);
}
boot().catch((e) => { console.error(e); app.loader.error(String(e)); });

// Hors ligne / installation (build de production seulement : le serveur de dev sert des modules à la volée).
if (import.meta.env.PROD && 'serviceWorker' in navigator) {
  window.addEventListener('load', () => { navigator.serviceWorker.register('/sw.js').catch((e) => console.warn('service worker', e)); });
}
